import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import TyreSchematicView from '@/components/pmv/TyreSchematicView';
import PmvInstallToPositionModal from '@/components/pmv/PmvInstallToPositionModal';
import pmvService from '@/services/pmv.service';
import type { PmvTyreSchematicCard } from '@/services/pmv.service';
import { formatHireEquipmentLabel } from '@/utils/pmvHireUi';

const { Text } = Typography;

const CONDITION_OPTS = [
  'NEW',
  'GOOD',
  'SERVICEABLE',
  'WORN',
  'DAMAGED',
  'REPAIRABLE',
  'RETREADABLE',
  'UNSERVICEABLE',
  'SCRAP',
].map((v) => ({ value: v, label: v }));

const DAMAGE_OPTS = [
  'CUT',
  'BULGE',
  'SIDEWALL',
  'PUNCTURE',
  'UNEVEN_WEAR',
  'BEAD',
  'CRACKING',
  'EXPOSED_CORD',
  'FOREIGN_OBJECT',
  'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

/**
 * Current tyre schematic for one PMV profile.
 * Route: /asset/pmv/:id/tyres — id is pmv_profile_id.
 */
const PmvTyreSchematicPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [manualId, setManualId] = useState<string | undefined>();
  const [equipSearch, setEquipSearch] = useState('');
  const profileId = id || manualId || '';
  const [inspectCard, setInspectCard] = useState<PmvTyreSchematicCard | null>(null);
  const [installCard, setInstallCard] = useState<PmvTyreSchematicCard | null>(null);
  const [form] = Form.useForm();
  const qc = useQueryClient();

  const profilesQ = useQuery({
    queryKey: ['pmv-schematic-profiles', equipSearch],
    queryFn: () =>
      pmvService.listProfiles({ page: 1, pageSize: 100, q: equipSearch || undefined }),
    enabled: !id,
    staleTime: 30_000,
  });

  const equipmentOptions = useMemo(
    () =>
      (profilesQ.data?.data ?? []).map((p) => ({
        value: p.id,
        label: formatHireEquipmentLabel(p),
      })),
    [profilesQ.data]
  );

  const q = useQuery({
    queryKey: ['pmv-tyre-schematic', profileId],
    queryFn: () => pmvService.getTyreSchematic(profileId),
    enabled: Boolean(profileId),
    retry: false,
  });

  const refreshSchematic = () => {
    void qc.invalidateQueries({ queryKey: ['pmv-tyre-schematic', profileId] });
    void qc.invalidateQueries({ queryKey: ['pmv-battery-schematic', profileId] });
  };

  const inspectMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      pmvService.recordTyreInspection(String(inspectCard?.component_id), body),
    onSuccess: () => {
      message.success('Inspection recorded');
      setInspectCard(null);
      form.resetFields();
      refreshSchematic();
    },
    onError: (e: Error) => message.error(e.message || 'Inspection failed'),
  });

  const onSelectCard = (card: PmvTyreSchematicCard) => {
    if (card.status === 'VACANT') setInstallCard(card);
    else if (card.component_id) setInspectCard(card);
  };

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Tyre schematic"
        subtitle="Operational axle/wheel map — tread, pressure (manual), damage"
        primaryActions={
          <Space wrap>
            {profileId ? (
              <Button>
                <Link to={`/asset/pmv/${profileId}`}>Equipment passport</Link>
              </Button>
            ) : null}
            <Button>
              <Link to="/asset/pmv/hub">PMV hub</Link>
            </Button>
          </Space>
        }
      />

      {!id && (
        <Card size="small" style={{ marginBottom: 16 }} title="Open by equipment">
          <Space wrap style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              placeholder="Search fleet / asset code"
              style={{ minWidth: 360, maxWidth: 520 }}
              value={manualId}
              onSearch={setEquipSearch}
              onChange={(v) => setManualId(v)}
              filterOption={false}
              options={equipmentOptions}
              notFoundContent={profilesQ.isLoading ? 'Loading…' : 'No equipment'}
            />
            <Button
              type="primary"
              disabled={!manualId}
              onClick={() => manualId && navigate(`/asset/pmv/${manualId}/tyres`)}
            >
              Open schematic
            </Button>
          </Space>
        </Card>
      )}

      {q.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load tyre schematic"
          description={(q.error as Error)?.message || 'Check permissions and layout.'}
        />
      )}

      <Card>
        {q.isLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (
          <TyreSchematicView
            data={q.data}
            pmvProfileId={profileId || undefined}
            allowVacantSelect
            onLayoutApplied={refreshSchematic}
            onSelectCard={onSelectCard}
          />
        )}
        {q.data?.note && (
          <Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12 }}>
            {q.data.note}
          </Text>
        )}
      </Card>

      <PmvInstallToPositionModal
        open={Boolean(installCard)}
        profileId={profileId}
        family="TYRE"
        position={
          installCard
            ? {
                position_id: installCard.position_id,
                position_code: installCard.position_code,
                display_label: installCard.display_label,
                family: 'TYRE',
              }
            : null
        }
        onClose={() => setInstallCard(null)}
        onInstalled={refreshSchematic}
      />

      <Modal
        title={`Inspect ${inspectCard?.component_number || ''} @ ${inspectCard?.position_code || ''}`}
        open={Boolean(inspectCard?.component_id)}
        onCancel={() => {
          setInspectCard(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={inspectMut.isPending}
        destroyOnClose
        width={520}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Manual inspection evidence — not live TPMS"
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            tread_inner: undefined,
            tread_center: undefined,
            tread_outer: undefined,
            pressure_psi: undefined,
            condition_code: inspectCard?.condition_code || undefined,
            damage_code: undefined,
            damage_severity: 'MEDIUM',
            notes: undefined,
          }}
          onFinish={(values) => {
            const points: Array<{ point_code: string; depth_mm: number }> = [];
            if (values.tread_inner != null) {
              points.push({ point_code: 'INNER', depth_mm: Number(values.tread_inner) });
            }
            if (values.tread_center != null) {
              points.push({ point_code: 'CENTER', depth_mm: Number(values.tread_center) });
            }
            if (values.tread_outer != null) {
              points.push({ point_code: 'OUTER', depth_mm: Number(values.tread_outer) });
            }
            const damages =
              values.damage_code != null && values.damage_code !== ''
                ? [
                    {
                      damage_code: values.damage_code,
                      severity: values.damage_severity || 'MEDIUM',
                    },
                  ]
                : [];
            inspectMut.mutate({
              condition_code: values.condition_code || null,
              pressure_psi: values.pressure_psi ?? null,
              tread_points: points,
              damages,
              notes: values.notes || null,
            });
          }}
        >
          <Form.Item name="condition_code" label="Condition">
            <Select allowClear options={CONDITION_OPTS} />
          </Form.Item>
          <Space wrap style={{ width: '100%' }} size="middle">
            <Form.Item name="tread_inner" label="Tread inner (mm)">
              <InputNumber min={0} step={0.1} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="tread_center" label="Tread center (mm)">
              <InputNumber min={0} step={0.1} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="tread_outer" label="Tread outer (mm)">
              <InputNumber min={0} step={0.1} style={{ width: 120 }} />
            </Form.Item>
          </Space>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            Minimum of measured points is stored (edge wear is not averaged away).
          </Text>
          <Form.Item name="pressure_psi" label="Pressure (psi)">
            <InputNumber min={0} step={0.5} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="damage_code" label="Damage">
            <Select allowClear options={DAMAGE_OPTS} />
          </Form.Item>
          <Form.Item name="damage_severity" label="Damage severity">
            <Select
              options={['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((v) => ({ value: v, label: v }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvTyreSchematicPage;
